/**
 * Crosstab Edit Modal
 *
 * レポート内のCrosstab要素を編集するためのモーダル
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { Dialog } from '../common/Dialog';
import { Button } from '../common/Button';
import type { CrosstabElement } from '../../../types/report.types';
import type { DataSet } from '../../../types/dataset.types';
import { hasData } from '../../../types/dataset.types';
import type {
  CrosstabConfiguration,
  AggregationFunction,
  CrosstabDataModel
} from '../../utils/crosstab/types';
import { CrosstabEngine } from '../../utils/crosstab/engine';
import { CrosstabPreview } from '../TabContainer/tabs/components/CrosstabPreview';
import logger from '../../utils/logger';
import * as styles from '../TabContainer/tabs/CrosstabTabContent.css';

interface CrosstabEditModalProps {
  isOpen: boolean;
  element: CrosstabElement;
  dataset: DataSet;
  onSave: (newConfiguration: CrosstabConfiguration) => void;
  onClose: () => void;
}

export function CrosstabEditModal({
  isOpen,
  element,
  dataset,
  onSave,
  onClose
}: CrosstabEditModalProps) {
  const { crosstabConfiguration } = element;

  // 編集中の設定
  const [editConfig, setEditConfig] = useState<CrosstabConfiguration>(crosstabConfiguration);
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewResult, setPreviewResult] = useState<CrosstabDataModel | null>(null);
  const [error, setError] = useState<string | null>(null);

  // 利用可能なカラム
  const availableColumns = useMemo(() => {
    return dataset?.schema?.columns || [];
  }, [dataset]);

  // 数値型カラムのみ（値フィールド用）
  const numericColumns = useMemo(() => {
    return availableColumns.filter(col => {
      const type = col.type.toLowerCase();
      return (
        type.includes('int') ||
        type.includes('float') ||
        type.includes('double') ||
        type.includes('boolean')
      );
    });
  }, [availableColumns]);

  // プレビュー生成
  const handleGeneratePreview = useCallback(async () => {
    if (!hasData(dataset)) {
      setError('Dataset does not have data loaded');
      return;
    }

    setIsGenerating(true);
    setError(null);

    try {
      const engine = new CrosstabEngine();
      const result = await engine.execute(dataset, editConfig);
      setPreviewResult(result);
      logger.info('[CrosstabEditModal] Preview generated successfully');
    } catch (err) {
      logger.error('[CrosstabEditModal] Error generating preview:', err);
      setError(err instanceof Error ? err.message : 'Failed to generate preview');
    } finally {
      setIsGenerating(false);
    }
  }, [dataset, editConfig]);

  // モーダルが開いたときに状態をリセットして初期プレビューを生成
  useEffect(() => {
    if (isOpen) {
      // 設定を現在のcrosstabConfigurationで初期化
      setEditConfig(crosstabConfiguration);
      setPreviewResult(null);
      setError(null);

      // 初期プレビューを生成（valueFieldsがある場合のみ）
      if (crosstabConfiguration.valueFields.length > 0) {
        const generateInitialPreview = async () => {
          if (!hasData(dataset)) {
            setError('Dataset does not have data loaded');
            return;
          }

          setIsGenerating(true);
          try {
            const engine = new CrosstabEngine();
            const result = await engine.execute(dataset, crosstabConfiguration);
            setPreviewResult(result);
            logger.info('[CrosstabEditModal] Initial preview generated');
          } catch (err) {
            logger.error('[CrosstabEditModal] Error generating initial preview:', err);
            setError(err instanceof Error ? err.message : 'Failed to generate preview');
          } finally {
            setIsGenerating(false);
          }
        };
        generateInitialPreview();
      }
    }
  }, [isOpen, crosstabConfiguration, dataset]);

  // 保存
  const handleSave = useCallback(() => {
    onSave(editConfig);
    onClose();
  }, [editConfig, onSave, onClose]);

  // 集計関数の追加
  const handleAddValueField = useCallback((fieldName: string) => {
    if (!fieldName) return;

    setEditConfig(prev => ({
      ...prev,
      valueFields: [
        ...prev.valueFields,
        {
          fieldId: availableColumns.find(c => c.name === fieldName)!.id,
          fieldName,
          aggregation: 'sum' as AggregationFunction,
          dataType: availableColumns.find(c => c.name === fieldName)!.type
        }
      ]
    }));
  }, [availableColumns]);

  // 集計関数の削除
  const handleRemoveValueField = useCallback((index: number) => {
    setEditConfig(prev => ({
      ...prev,
      valueFields: prev.valueFields.filter((_, i) => i !== index)
    }));
  }, []);

  // 集計関数の変更
  const handleChangeAggregation = useCallback((index: number, aggregation: AggregationFunction) => {
    setEditConfig(prev => ({
      ...prev,
      valueFields: prev.valueFields.map((vf, i) =>
        i === index ? { ...vf, aggregation } : vf
      )
    }));
  }, []);

  // Row/Column フィールドの追加
  const handleAddRowField = useCallback((fieldName: string) => {
    if (!fieldName) return;

    const column = availableColumns.find(c => c.name === fieldName);
    if (!column) return;

    setEditConfig(prev => ({
      ...prev,
      rowFields: [
        ...prev.rowFields,
        {
          fieldId: column.id,
          fieldName,
          dataType: column.type,
          sortOrder: 'data' as const
        }
      ]
    }));
  }, [availableColumns]);

  const handleAddColumnField = useCallback((fieldName: string) => {
    if (!fieldName) return;

    const column = availableColumns.find(c => c.name === fieldName);
    if (!column) return;

    setEditConfig(prev => ({
      ...prev,
      columnFields: [
        ...prev.columnFields,
        {
          fieldId: column.id,
          fieldName,
          dataType: column.type,
          sortOrder: 'data' as const
        }
      ]
    }));
  }, [availableColumns]);

  // Row/Column フィールドの削除
  const handleRemoveRowField = useCallback((index: number) => {
    setEditConfig(prev => ({
      ...prev,
      rowFields: prev.rowFields.filter((_, i) => i !== index)
    }));
  }, []);

  const handleRemoveColumnField = useCallback((index: number) => {
    setEditConfig(prev => ({
      ...prev,
      columnFields: prev.columnFields.filter((_, i) => i !== index)
    }));
  }, []);

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Crosstab"
      width="xl"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleSave}>
            Save
          </Button>
        </>
      }
    >
      <div style={{ display: 'flex', gap: '16px', height: '600px' }}>
        {/* 設定パネル */}
        <div style={{ width: '300px', overflowY: 'auto', borderRight: '1px solid #e2e8f0', paddingRight: '16px' }}>
          {/* Row Fields */}
          <div className={styles.sectionTitle}>Row Fields</div>
          <div className={styles.fieldList}>
            {editConfig.rowFields.map((rf, index) => (
              <div key={index} className={styles.valueField}>
                <span>{rf.fieldName}</span>
                <button
                  onClick={() => handleRemoveRowField(index)}
                  className={styles.removeButton}
                  title="Remove field"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <select
            onChange={(e) => {
              handleAddRowField(e.target.value);
              e.target.value = '';
            }}
            className={styles.addFieldSelect}
            value=""
          >
            <option value="">+ Add row field</option>
            {availableColumns.map(col => (
              <option key={col.id} value={col.name}>
                {col.name}
              </option>
            ))}
          </select>

          {/* Column Fields */}
          <div className={styles.sectionTitle} style={{ marginTop: '16px' }}>Column Fields</div>
          <div className={styles.fieldList}>
            {editConfig.columnFields.map((cf, index) => (
              <div key={index} className={styles.valueField}>
                <span>{cf.fieldName}</span>
                <button
                  onClick={() => handleRemoveColumnField(index)}
                  className={styles.removeButton}
                  title="Remove field"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <select
            onChange={(e) => {
              handleAddColumnField(e.target.value);
              e.target.value = '';
            }}
            className={styles.addFieldSelect}
            value=""
          >
            <option value="">+ Add column field</option>
            {availableColumns.map(col => (
              <option key={col.id} value={col.name}>
                {col.name}
              </option>
            ))}
          </select>

          {/* Value Fields */}
          <div className={styles.sectionTitle} style={{ marginTop: '16px' }}>Value Fields</div>
          <div className={styles.fieldList}>
            {editConfig.valueFields.map((vf, index) => (
              <div key={index} className={styles.valueField}>
                <span>{vf.fieldName}</span>
                <select
                  value={vf.aggregation}
                  onChange={(e) => handleChangeAggregation(index, e.target.value as AggregationFunction)}
                  className={styles.aggregationSelect}
                >
                  <option value="sum">SUM</option>
                  <option value="avg">AVG</option>
                  <option value="count">COUNT</option>
                  <option value="min">MIN</option>
                  <option value="max">MAX</option>
                </select>
                <button
                  onClick={() => handleRemoveValueField(index)}
                  className={styles.removeButton}
                  title="Remove field"
                >
                  ×
                </button>
              </div>
            ))}
          </div>
          <select
            onChange={(e) => {
              handleAddValueField(e.target.value);
              e.target.value = '';
            }}
            className={styles.addFieldSelect}
            value=""
          >
            <option value="">+ Add value field</option>
            {numericColumns.map(col => (
              <option key={col.id} value={col.name}>
                {col.name}
              </option>
            ))}
          </select>

          <div style={{ marginTop: '16px' }}>
            <Button
              variant="primary"
              onClick={handleGeneratePreview}
              disabled={isGenerating || editConfig.valueFields.length === 0}
            >
              {isGenerating ? 'Generating...' : 'Update Preview'}
            </Button>
          </div>

          {error && (
            <div className={styles.error}>{error}</div>
          )}
        </div>

        {/* プレビューエリア */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          {previewResult ? (
            <CrosstabPreview
              result={previewResult}
              sortConfig={undefined}
              isStale={false}
            />
          ) : (
            <div className={styles.placeholder}>
              Click "Update Preview" to see the result
            </div>
          )}
        </div>
      </div>
    </Dialog>
  );
}
